export function normalizeFilterText(value?: string | number | null) {
  return String(value ?? "")
    .trim()
    .toLocaleLowerCase("tr-TR");
}

export function matchesContains(value: string | number | null | undefined, query: string) {
  if (!query.trim()) return true;
  return normalizeFilterText(value).includes(normalizeFilterText(query));
}

export function matchesExact(value: string | number | null | undefined, query: string) {
  if (!query.trim()) return true;
  return normalizeFilterText(value) === normalizeFilterText(query);
}

export function matchesAnyOf(value: string | number | null | undefined, selections: string[]) {
  if (!selections.length) return true;
  const normalizedValue = normalizeFilterText(value);
  return selections.some((selection) => normalizeFilterText(selection) === normalizedValue);
}

export function uniqueFilterOptions(values: Array<string | number | null | undefined>) {
  return Array.from(
    new Set(
      values
        .map((value) => String(value ?? "").trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b, "tr"));
}

export interface NumericRangeFilter {
  min: string;
  max: string;
}

export function parseLocalizedNumber(value: string | number | null | undefined) {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;

  const raw = String(value ?? "").trim();
  if (!raw) return 0;

  const cleaned = raw.replace(/[^\d,.-]/g, "");
  if (!cleaned || cleaned === "-" || cleaned === "," || cleaned === ".") return 0;

  const negative = cleaned.startsWith("-");
  const unsigned = cleaned.replace(/-/g, "");
  let normalized = "";

  if (unsigned.includes(",")) {
    const lastComma = unsigned.lastIndexOf(",");
    const integerPart = unsigned.slice(0, lastComma).replace(/\D/g, "");
    const decimalPart = unsigned.slice(lastComma + 1).replace(/\D/g, "").slice(0, 2);
    normalized = `${integerPart || "0"}${decimalPart ? `.${decimalPart}` : ""}`;
  } else if (unsigned.includes(".")) {
    const parts = unsigned.split(".");
    const lastPart = parts[parts.length - 1] ?? "";
    const looksLikeThousands = parts.length > 1 && lastPart.length === 3;
    normalized = looksLikeThousands
      ? parts.join("").replace(/\D/g, "")
      : unsigned.replace(/[^\d.]/g, "");
  } else {
    normalized = unsigned.replace(/\D/g, "");
  }

  const parsed = Number(`${negative ? "-" : ""}${normalized}`);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function matchesNumericRange(
  value: string | number | null | undefined,
  range?: NumericRangeFilter | null
) {
  if (!range) return true;

  const hasMin = range.min.trim().length > 0;
  const hasMax = range.max.trim().length > 0;
  if (!hasMin && !hasMax) return true;

  const numericValue = parseLocalizedNumber(value);
  if (!Number.isFinite(numericValue)) return false;

  const minValue = hasMin ? parseLocalizedNumber(range.min) : Number.NEGATIVE_INFINITY;
  const maxValue = hasMax ? parseLocalizedNumber(range.max) : Number.POSITIVE_INFINITY;

  if (Number.isFinite(minValue) && numericValue < minValue) return false;
  if (Number.isFinite(maxValue) && numericValue > maxValue) return false;
  return true;
}

export type SortDirection = "asc" | "desc";

export function compareTextForSort(a: string | number | null | undefined, b: string | number | null | undefined) {
  return String(a ?? "").localeCompare(String(b ?? ""), "tr", {
    numeric: true,
    sensitivity: "base",
  });
}

export function compareNumberForSort(a: string | number | null | undefined, b: string | number | null | undefined) {
  const left = parseLocalizedNumber(a);
  const right = parseLocalizedNumber(b);
  return (Number.isFinite(left) ? left : 0) - (Number.isFinite(right) ? right : 0);
}

export function applySortDirection(result: number, direction: SortDirection) {
  return direction === "asc" ? result : -result;
}
